// יקום (01): עולם, חברה, תבנית, סוד; תבנית עולם (04) ואדפטר עולם חי (08).
import { z } from 'zod';
import { Id, Json, Ts, rowBase } from './common.ts';

export const WorldType = z.enum(['priority', 'hashavshevet', 'monday', 'synthetic', 'custom']);
export type WorldType = z.infer<typeof WorldType>;

export const WorldMode = z.enum(['etl', 'live']);
export type WorldMode = z.infer<typeof WorldMode>;

export const WorldAdapterKind = z.enum(['connector', 'cloud']);
export type WorldAdapterKind = z.infer<typeof WorldAdapterKind>;

export const WorldStatus = z.enum(['draft', 'active', 'paused', 'deleted']);
export type WorldStatus = z.infer<typeof WorldStatus>;

/** מפתח עולם — סיומת הסכימה `etl_<key>`. */
export const WorldKey = z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'world key must match [a-z][a-z0-9_]{1,30}');

/** חברה בעולם; `db` תיעודי בלבד (הסמכות אצל הקונקטור). */
export const Company = z.object({
  code: z.string().min(1),
  nameHe: z.string(),
  db: z.string().optional(),
});
export type Company = z.infer<typeof Company>;

/** sys.world */
export const World = z.object({
  ...rowBase,
  key: WorldKey,
  nameHe: z.string(),
  type: WorldType,
  mode: WorldMode,
  adapter: WorldAdapterKind,
  adapterConfig: z.record(z.string(), z.unknown()),
  credentialsRef: z.string().nullable(),
  companies: z.array(Company),
  status: WorldStatus,
  /** מחיקה רכה (05 world.delete). */
  deletedAt: Ts.nullable(),
  deletedReason: z.string().nullable(),
});
export type World = z.infer<typeof World>;

/** גרסת תבנית — semver (data-world-templates). */
export const TemplateVersion = z.string().regex(/^\d+\.\d+\.\d+$/, 'template version must be semver (x.y.z)');
/**
 * תבנית סוג עולם (04 §1, data-world-templates): אותו טיפוס לכל סוג עולם עם תבנית (פריוריטי, חשבשבת…). חבילת התבנית
 * (`packages/template-<worldType>`) מעדינה את תתי-המבנים (`.extend`) ומאמתת מולו; כאן הצורה העליונה. `custom` אינו סוג עם תבנית.
 */
export const WorldTemplate = z.object({
  worldType: WorldType.exclude(['custom']),
  version: TemplateVersion,
  dialects: z.array(z.enum(['mssql', 'oracle'])).min(1),
  knowledge: z.record(z.string(), z.unknown()),
  semantics: z.record(z.string(), z.unknown()),
  dictionary: z.record(z.string(), z.unknown()),
  syncDefaults: z.record(z.string(), z.unknown()),
  /**
   * data-priority-field-type-direction: prior לכיוון ערכי טקסט לפי טיפוס המילון (CHAR/RCHAR…) — מה שנמדד במופעי הייחוס.
   * אופציונלי (רק לתבניות שיש בהן טיפוס מילון); במופע נדרס במה שנמדד בו (`sys.direction_evidence`).
   */
  directionByType: z.record(z.string(), z.enum(['visual', 'logical', 'unknown'])).optional(),
});
export type WorldTemplate = z.infer<typeof WorldTemplate>;

/**
 * מה מנהל בינה עושה עם שינוי בתבנית: none — לידיעה; review_tree — bootstrap ממוקד על affects.entities; review_spreads — הפריסות
 * שנוגעות בישויות; rescan_direction — "עדכן תבנית" (template.apply) מריץ סריקת כיוון מתקנת (data-priority-field-type-direction).
 */
export const TemplateChangeAction = z.enum(['none', 'review_tree', 'review_spreads', 'rescan_direction']);
export type TemplateChangeAction = z.infer<typeof TemplateChangeAction>;
/** שינוי אחד ברשימת השינויים של תבנית (`data/changelog.json`). */
export const TemplateChange = z
  .object({
    area: z.string().min(1),
    summaryHe: z.string().min(1),
    affects: z.object({ entities: z.array(z.string()).optional(), measures: z.array(z.string()).optional(), rules: z.array(z.string()).optional() }).strict(),
    action: TemplateChangeAction,
  })
  .strict();
export type TemplateChange = z.infer<typeof TemplateChange>;
/** רשומת גרסה ב-changelog: contentHash = sha256 מנורמל של קבצי data/ פרט ל-changelog. */
export const TemplateChangelogEntry = z
  .object({
    version: TemplateVersion,
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    contentHash: z.string().regex(/^[0-9a-f]{64}$/),
    changes: z.array(TemplateChange).min(1),
  })
  .strict();
export type TemplateChangelogEntry = z.infer<typeof TemplateChangelogEntry>;

/** השוואת semver: שלילי / 0 / חיובי. */
export function compareTemplateVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  return 0;
}
/** רשימת שינויים: לפחות רשומה אחת, גרסאות עולות ממש. */
export const TemplateChangelog = z
  .array(TemplateChangelogEntry)
  .min(1)
  .superRefine((log, ctx) => {
    for (let i = 1; i < log.length; i++)
      if (compareTemplateVersions(log[i]!.version, log[i - 1]!.version) <= 0)
        ctx.addIssue({ code: 'custom', path: [i, 'version'], message: `changelog: ${log[i]!.version} אינה גבוהה מ-${log[i - 1]!.version}, רשומות בסדר עולה` });
  });
export type TemplateChangelog = z.infer<typeof TemplateChangelog>;

/** הרשומות בטווח (from, to] — from חסר = מההתחלה; to חסר = עד האחרונה. */
export function templateChangesBetween(log: readonly TemplateChangelogEntry[], from?: string | null, to?: string | null): TemplateChangelogEntry[] {
  return log.filter((e) => (!from || compareTemplateVersions(e.version, from) > 0) && (!to || compareTemplateVersions(e.version, to) <= 0));
}

/** הגרסה בקוד חייבת להיות הרשומה האחרונה ב-changelog — אחרת טעינת התבנית נכשלת. */
export function assertTemplateVersion(version: string, log: readonly TemplateChangelogEntry[]): void {
  const last = log[log.length - 1];
  if (!last || last.version !== version)
    throw new Error(`גרסת התבנית ${version} אינה הרשומה האחרונה ב-changelog (${last?.version ?? 'ריק'}). העלה גרסה והוסף שורה ל-changelog`);
}

/** sys.template — שורה שנטענה מ-packages/template-*. */
export const Template = z.object({
  ...rowBase,
  worldType: WorldType,
  version: z.string(),
  content: Json,
  /** מתי הגרסה הזו של התבנית נטענה לראשונה (data-world-templates) — היסטוריית הגרסאות. */
  installedAt: Ts,
});
export type Template = z.infer<typeof Template>;

/** sys.secret — ciphertext לעולם לא יוצא מ-packages/db (getSecret מחזיר plaintext בלבד). */
export const Secret = z.object({
  ...rowBase,
  ref: z.string().min(1),
  ciphertext: z.instanceof(Uint8Array),
  nonce: z.instanceof(Uint8Array),
  rotatedAt: Ts.nullable(),
});
export type Secret = z.infer<typeof Secret>;

// ---------- עולם חי (08 §1) ----------

/** http דק שה-egress-proxy מזריק לו credential; מסופק ע"י קונטיינר האדפטר. */
export type AdapterHttp = {
  get(path: string, init?: { headers?: Record<string, string> }): Promise<unknown>;
  post(path: string, body?: unknown, init?: { headers?: Record<string, string> }): Promise<unknown>;
  request(method: string, path: string, body?: unknown, init?: { headers?: Record<string, string> }): Promise<unknown>;
};

export type WorldAdapterFunction = {
  input: z.ZodType;
  output: z.ZodType;
  descriptionHe: string;
  /** true ⇒ קונסנט/idempotency/replay (08); false ⇒ קריאה, נרשמת בלי replay. */
  sideEffect: boolean;
  run: (args: unknown, http: AdapterHttp) => Promise<unknown>;
};

const isZod = (v: unknown): v is z.ZodType => v instanceof z.ZodType;

export const WorldAdapterFunction = z.object({
  input: z.custom<z.ZodType>(isZod, 'input must be a zod schema'),
  output: z.custom<z.ZodType>(isZod, 'output must be a zod schema'),
  descriptionHe: z.string().min(1),
  sideEffect: z.boolean(),
  run: z.custom<WorldAdapterFunction['run']>((v) => typeof v === 'function', 'run must be a function'),
});

/** מודול האדפטר שבינה-1 כותבת (`export const adapter: WorldAdapter`). */
export const WorldAdapter = z.object({
  world: z.string(),
  baseUrl: z.url({ protocol: /^https$/ }),
  functions: z.record(z.string(), WorldAdapterFunction),
});
export type WorldAdapter = {
  world: string;
  baseUrl: string;
  functions: Record<string, WorldAdapterFunction>;
};

/** sys.world_adapter — גרסת קוד אדפטר שמורה. */
export const WorldAdapterVersion = z.object({
  ...rowBase,
  worldId: Id,
  version: z.number().int().positive(),
  code: z.string(),
  createdBy: Id,
  noteHe: z.string().nullable(),
});
export type WorldAdapterVersion = z.infer<typeof WorldAdapterVersion>;
